import { Autocomplete, Box, TextField } from '@mui/material';
import { useThrottledValue } from '@tanstack/react-pacer';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Geocoder } from '@/modules/Geocoder';
import { geocoderApiV1AutocompleteOptions } from '@/modules/api/geocoder/@tanstack/react-query.gen';
import { useRoutesStore } from '@/stores/useRoutesStore';
import type { LocationFeature } from '@/types/LocationFeature';
import type { RouteLocationType } from '@/types/RouteLocationType';

interface GeocodingSearchInputProps {
  label: string;
  routeLocationType: RouteLocationType;
}

export function GeocodingSearchInput({ label, routeLocationType }: GeocodingSearchInputProps) {
  const value = useRoutesStore((state) => state.routeLocations[routeLocationType]);
  const { updateRouteLocations } = useRoutesStore((state) => state.actions);

  const [inputValue, setInputValue] = useState('');
  const [throttledInputValue] = useThrottledValue(inputValue, { wait: 300 });
  const [focus, setFocus] = useState(false);
  const [highlight, setHighlight] = useState<LocationFeature | null>(null);

  const { data: options = [] } = useQuery({
    ...geocoderApiV1AutocompleteOptions({
      query: {
        text: throttledInputValue,
        sources: Geocoder.SOURCES,
        'boundary.rect.min_lon': 7.3,
        'boundary.rect.max_lon': 10.7,
        'boundary.rect.min_lat': 49.1,
        'boundary.rect.max_lat': 51.95,
        'focus.point.lat': 50.12125,
        'focus.point.lon': 8.636583,
        size: 10,
      },
    }),
    enabled: focus && throttledInputValue !== '',
    placeholderData: keepPreviousData,
    select: (data) => data.features.map((f) => Geocoder.transformToLocationFeature(f)),
  });

  const optionsIncludeValue = value && options.find((option) => Geocoder.areSame(option, value));
  const autocompleteOptions = [...options];

  if (value && !optionsIncludeValue) {
    autocompleteOptions.unshift(value);
  }

  const handleChange = (newLocation: LocationFeature | null) => {
    setFocus(!newLocation);
    updateRouteLocations({
      routeLocationType,
      location: newLocation ?? undefined,
    });
  };

  return (
    <Autocomplete
      autoComplete
      autoSelect
      blurOnSelect
      includeInputInList
      defaultValue={null}
      filterOptions={(x) => x}
      filterSelectedOptions={!optionsIncludeValue}
      forcePopupIcon={false}
      inputValue={inputValue}
      isOptionEqualToValue={Geocoder.areSame}
      onBlur={() => setFocus(false)}
      onChange={(event, newLocation) => handleChange(newLocation)}
      onFocus={() => setFocus(true)}
      options={autocompleteOptions}
      value={value ?? null}
      getOptionLabel={({
        properties: {
          name: { full },
        },
      }) => full}
      onHighlightChange={(event, option) => {
        setHighlight(option);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.currentTarget.querySelector('input') === document.activeElement) {
          handleChange(options[0]);
          event.currentTarget.querySelector('input')?.blur();
        }
        if (event.key === 'Tab') {
          const location = highlight ?? options[0];

          if (location && inputValue !== location.properties.name.firstLine) {
            event.preventDefault();
            setInputValue(location.properties.name.firstLine);
          }
        }
      }}
      renderInput={(params) => (
        <TextField placeholder={label} sx={{ marginTop: 1 }} variant="standard" {...params} fullWidth />
      )}
      renderOption={(props, location) => {
        const {
          properties: {
            name: { firstLine, secondLine },
          },
        } = location;

        const key =
          location.properties.peliasGid ?? `${location.geometry.coordinates[0]}-${location.geometry.coordinates[1]}`;

        return (
          <li {...props} key={key}>
            <Box>
              {firstLine}
              {secondLine ? <Box sx={{ fontSize: '0.75rem', lineHeight: 1.1 }}>{secondLine}</Box> : null}
            </Box>
          </li>
        );
      }}
    />
  );
}
